import { useEffect, useState } from "react";
import { getSessionPath } from "../lib/apiPaths";
import { emitSpan } from "../lib/tracing";
import { setSession, type AuthSession } from "../lib/session";

type SessionUser = {
  id: string;
  email: string;
  tenantId: string;
  tenantOrigin: string;
  tenantEdgeOrigin: string;
  tenantEdgeWildcardDomain: string | null;
  tenantCustomDomains: string[];
  tenantApiOrigin: string;
};

export function useSessionBootstrap() {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const start = performance.now();
      let delay = 500;
      let attemptCount = 0;
      const retry = async () => {
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = Math.min(delay * 2, 10_000);
      };
      while (!cancelled) {
        try {
          attemptCount++;
          const response = await fetch(getSessionPath(), {
            credentials: "include",
          });
          if (response.status >= 500) {
            await retry();
            continue;
          }
          const session = response.ok
            ? ((await response.json()) as AuthSession)
            : null;
          if (cancelled) return;

          const duration = performance.now() - start;
          emitSpan(
            "ui.session.bootstrap",
            {
              "session.bootstrap.duration_ms": Math.round(duration),
              "session.bootstrap.signed_in": session?.user ? 1 : 0,
              "session.bootstrap.attempt_count": attemptCount,
            },
            duration
          );
          setSession(session);
          if (session?.user && session.tenantId) {
            setUser({
              ...session.user,
              tenantId: session.tenantId,
              tenantOrigin: session.tenantOrigin,
              tenantEdgeOrigin: session.tenantPublicOrigin,
              tenantEdgeWildcardDomain: session.tenantPublicWildcardDomain,
              tenantCustomDomains: session.tenantCustomDomains ?? [],
              tenantApiOrigin: session.tenantApiOrigin ?? session.tenantOrigin,
            });
          } else {
            setUser(null);
          }
          setHasLoaded(true);
          return;
        } catch {
          await retry();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { user, hasLoaded };
}
